import React, { useState } from 'react';
import {
  X, Shield, Clock, Plane, Users, Star, CheckCircle2,
  Lock, ArrowRight, Hotel, Calendar, MapPin
} from 'lucide-react';
import { formatPrice } from '../data/mockData';

const BADGE_COLORS = {
  'BEST SELLER': 'bg-[#F5A623] text-[#0E0E12]',
  'POPULER': 'bg-[#00C9B1] text-[#0E0E12]',
  'VIP': 'bg-purple-500 text-white',
  'HEMAT': 'bg-green-500 text-white',
  'KELUARGA': 'bg-blue-500 text-white',
  'EKSKLUSIF': 'bg-gradient-to-r from-[#F5A623] to-[#FDC15B] text-[#0E0E12]',
};

const EXTRA_INCLUSIONS = [
  'Pembimbing Ibadah Bersertifikat Kemenag',
  'Transportasi Makkah-Madinah PP (Full AC)',
  'Makan 3x Sehari (Menu Indonesia & Arab)',
  'Perlengkapan Umroh (Koper, Ihram/Mukena)',
  'Asuransi Perjalanan & Kesehatan',
  'Kartu SIM Arab Saudi + Data 5GB',
];

function StarRating({ rating }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star
          key={i}
          size={13}
          className={i <= Math.round(rating) ? 'text-[#F5A623] fill-[#F5A623]' : 'dark:text-gray-600 text-gray-300'}
        />
      ))}
    </div>
  );
}

function SuccessView({ pkg, onClose }) {
  const orderNumber = `ORD-2026-${Math.floor(Math.random() * 90000 + 10000)}`;
  return (
    <div className="p-8 text-center">
      <div className="w-16 h-16 bg-[#00C9B1]/15 rounded-full flex items-center justify-center mx-auto mb-5">
        <CheckCircle2 size={32} className="text-[#00C9B1]" />
      </div>
      <h3 className="text-xl font-black dark:text-white text-[#1A202C] mb-2">Paket Berhasil Dipesan!</h3>
      <p className="text-sm dark:text-gray-300 text-gray-600 font-medium mb-1">{pkg.name}</p>
      <p className="text-xs dark:text-gray-500 text-gray-400 mb-1">{pkg.provider}</p>

      <div className="my-5 px-4 py-3 rounded-xl dark:bg-[#242430] bg-[#F7FAFC] inline-block">
        <p className="text-xs dark:text-gray-500 text-gray-400 mb-0.5">No. Pesanan</p>
        <p className="font-mono font-bold text-[#00C9B1] text-base">{orderNumber}</p>
      </div>

      <p className="text-sm dark:text-gray-400 text-gray-500 mb-6 leading-relaxed max-w-xs mx-auto">
        Tim kami akan menghubungi Anda dalam <span className="font-semibold dark:text-white text-[#1A202C]">1x24 jam</span> untuk konfirmasi dan proses Smart Escrow.
      </p>

      <div className="space-y-3">
        <button
          data-testid="booking-tracker-btn"
          onClick={onClose}
          className="w-full py-3 bg-[#00C9B1] text-[#0E0E12] font-bold rounded-xl hover:brightness-110 transition-all flex items-center justify-center gap-2"
        >
          <MapPin size={16} />
          Cek Status di Journey Tracker
        </button>
        <button
          onClick={onClose}
          className="w-full py-3 dark:text-gray-400 text-gray-500 text-sm hover:dark:text-white hover:text-[#1A202C] transition-colors"
        >
          Kembali ke Marketplace
        </button>
      </div>
    </div>
  );
}

export default function PackageDetailModal({ pkg, isAuthenticated, onClose, onLoginRequired }) {
  const [booked, setBooked] = useState(false);
  const [imgError, setImgError] = useState(false);

  if (!pkg) return null;

  const handleBook = () => {
    if (!isAuthenticated) {
      onClose();
      onLoginRequired && onLoginRequired();
    } else {
      setBooked(true);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 animate-fade-in">
      <div
        className="absolute inset-0 bg-[#0E0E12]/80 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        data-testid="package-detail-modal"
        className="relative w-full max-w-2xl dark:bg-[#1A1A24] bg-white rounded-2xl shadow-2xl animate-fade-in-up overflow-hidden"
        style={{ maxHeight: '90vh', overflowY: 'auto' }}
      >
        {booked ? (
          <SuccessView pkg={pkg} onClose={onClose} />
        ) : (
          <>
            {/* Hero Image */}
            <div className="relative h-56 sm:h-64 flex-shrink-0 dark:bg-[#242430] bg-gray-200">
              {!imgError ? (
                <img
                  src={pkg.image}
                  alt={pkg.name}
                  className="w-full h-full object-cover"
                  onError={() => setImgError(true)}
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-[#1A1A24] to-[#242430] flex items-center justify-center">
                  <MapPin size={40} className="text-[#2D3748]" />
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0E0E12]/90 via-[#0E0E12]/30 to-[#0E0E12]/10" />

              {/* Close button */}
              <button
                data-testid="close-detail-modal"
                onClick={onClose}
                className="absolute top-4 right-4 w-9 h-9 bg-[#0E0E12]/70 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-[#0E0E12] transition-colors"
              >
                <X size={16} />
              </button>

              {/* Badge */}
              <span className={`absolute top-4 left-4 px-2.5 py-1 rounded-full text-[10px] font-black tracking-wider ${BADGE_COLORS[pkg.badge] || 'bg-gray-600 text-white'}`}>
                {pkg.badge}
              </span>

              {/* Package info on image */}
              <div className="absolute bottom-4 left-5 right-5">
                <p className="text-xs text-gray-300 mb-1">{pkg.provider}</p>
                <h2 className="text-xl sm:text-2xl font-black text-white leading-tight">{pkg.name}</h2>
              </div>
            </div>

            {/* Body */}
            <div className="p-5 sm:p-6 space-y-5">

              {/* Trust Score + Rating */}
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 px-3 py-2 bg-[#F5A623]/10 border border-[#F5A623]/20 rounded-xl">
                  <Shield size={15} className="text-[#F5A623]" />
                  <span className="text-sm font-black text-[#F5A623]">Trust Score: {pkg.trustScore}</span>
                </div>
                <div className="flex items-center gap-2">
                  <StarRating rating={pkg.rating} />
                  <span className="text-sm font-semibold dark:text-gray-300 text-gray-600">{pkg.rating}</span>
                  <span className="text-sm dark:text-gray-500 text-gray-400">({pkg.reviewCount} ulasan)</span>
                </div>
                <span className="ml-auto text-xs dark:text-gray-500 text-gray-400">{pkg.sold}+ jamaah terdaftar</span>
              </div>

              {/* Quick Stats Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { icon: Clock, label: 'Durasi', value: pkg.duration, color: 'text-[#00C9B1]' },
                  { icon: Plane, label: 'Maskapai', value: pkg.flight, color: 'text-[#00C9B1]' },
                  { icon: Calendar, label: 'Berangkat', value: pkg.departure, color: 'text-[#F5A623]' },
                  { icon: Users, label: 'Sisa Kursi', value: `${pkg.seats} kursi`, color: pkg.seats <= 5 ? 'text-red-400' : 'text-[#00C9B1]' },
                ].map(item => {
                  const Icon = item.icon;
                  return (
                    <div key={item.label} className="dark:bg-[#242430] bg-[#F7FAFC] rounded-xl p-3">
                      <Icon size={13} className={`${item.color} mb-1.5`} strokeWidth={1.5} />
                      <p className="text-[10px] dark:text-gray-500 text-gray-400 mb-0.5 uppercase tracking-wide">{item.label}</p>
                      <p className="text-xs font-semibold dark:text-gray-200 text-[#2D3748] leading-snug">{item.value}</p>
                    </div>
                  );
                })}
              </div>

              {/* Price */}
              <div className="flex items-end justify-between pb-4 border-b dark:border-[#2D3748] border-[#E2E8F0]">
                <div>
                  <p className="text-xs dark:text-gray-500 text-gray-400 mb-1">Harga per jamaah</p>
                  <p className="text-3xl sm:text-4xl font-black text-[#F5A623]">{formatPrice(pkg.price)}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs dark:text-gray-500 text-gray-400 mb-1">Uang Muka (DP) 20%</p>
                  <p className="text-lg font-bold dark:text-white text-[#1A202C]">{formatPrice(pkg.price * 0.2)}</p>
                </div>
              </div>

              {/* Inclusions */}
              <div>
                <h4 className="font-bold dark:text-white text-[#1A202C] text-sm mb-3 flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-[#00C9B1]" />
                  Yang Sudah Termasuk
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[...pkg.highlights, ...EXTRA_INCLUSIONS.slice(0, 3)].map((item, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <CheckCircle2 size={13} className="text-[#00C9B1] flex-shrink-0 mt-0.5" />
                      <span className="text-xs dark:text-gray-300 text-[#4A5568] leading-relaxed">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Hotel */}
              <div className="flex items-center gap-3 p-4 rounded-2xl dark:bg-[#242430] bg-[#F7FAFC]">
                <div className="w-10 h-10 rounded-xl bg-[#00C9B1]/10 flex items-center justify-center flex-shrink-0">
                  <Hotel size={18} className="text-[#00C9B1]" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] dark:text-gray-500 text-gray-400 uppercase tracking-wider mb-0.5">Hotel Utama Makkah</p>
                  <p className="font-semibold dark:text-white text-[#1A202C] text-sm truncate">{pkg.hotel}</p>
                </div>
                <div className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].slice(0, pkg.trustScore >= 98 ? 5 : pkg.trustScore >= 95 ? 5 : 4).map(s => (
                    <Star key={s} size={12} className="text-[#F5A623] fill-[#F5A623]" />
                  ))}
                </div>
              </div>

              {/* Smart Escrow badge */}
              <div className="flex items-start gap-3 p-4 rounded-2xl bg-[#00C9B1]/5 border border-[#00C9B1]/15">
                <Lock size={16} className="text-[#00C9B1] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <div>
                  <p className="text-sm font-semibold text-[#00C9B1] mb-0.5">Dilindungi Smart Escrow OJK</p>
                  <p className="text-xs dark:text-gray-400 text-gray-500 leading-relaxed">
                    Dana Anda tersimpan aman di rekening escrow berlisensi. Hanya dicairkan sesuai milestone perjalanan yang terpenuhi.
                  </p>
                </div>
              </div>

              {/* CTA */}
              <div className="flex gap-3 pt-1">
                <button
                  data-testid="book-package-btn"
                  onClick={handleBook}
                  className="flex-1 py-4 bg-[#F5A623] text-[#0E0E12] font-bold rounded-2xl hover:brightness-110 transition-all hover:-translate-y-0.5 shadow-lg shadow-[#F5A623]/20 flex items-center justify-center gap-2 text-sm"
                >
                  {isAuthenticated ? 'Pesan Sekarang' : 'Masuk untuk Memesan'}
                  <ArrowRight size={16} />
                </button>
                <button
                  data-testid="close-modal-btn"
                  onClick={onClose}
                  className="px-5 py-4 rounded-2xl dark:bg-[#242430] bg-[#F7FAFC] dark:text-gray-400 text-gray-500 dark:hover:text-white hover:text-[#1A202C] transition-colors text-sm font-medium"
                >
                  Tutup
                </button>
              </div>

              {!isAuthenticated && (
                <p className="text-center text-xs dark:text-gray-500 text-gray-400 pb-1">
                  Belum punya akun? <span className="text-[#00C9B1] font-medium cursor-pointer hover:underline">Daftar gratis</span> — tidak ada biaya pendaftaran
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
